import { z } from 'zod';
import { codePointLength } from '@/lib/forms/primitives';
import type { Translate } from './login.schema';

/** i18n key suffixes for the five password rules, in display order. */
export const PASSWORD_RULE_KEYS = [
  'minLength',
  'hasUpperCase',
  'hasLowerCase',
  'hasNumber',
  'hasSpecial',
] as const;

export type PasswordRuleKey = (typeof PASSWORD_RULE_KEYS)[number];

const RULE_TESTS: Record<PasswordRuleKey, (password: string) => boolean> = {
  // Code points, not `.length`: Pydantic's min_length=8 counts Python characters.
  minLength: (p) => codePointLength(p) >= 8,
  hasUpperCase: (p) => /[A-Z]/.test(p),
  hasLowerCase: (p) => /[a-z]/.test(p),
  hasNumber: (p) => /[0-9]/.test(p),
  // eslint-disable-next-line no-useless-escape
  hasSpecial: (p) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(p),
};

/** Which of the five rules the password currently satisfies (for the live checklist). */
export function evaluatePasswordRules(
  password: string,
): Record<PasswordRuleKey, boolean> {
  return PASSWORD_RULE_KEYS.reduce(
    (acc, key) => ({ ...acc, [key]: RULE_TESTS[key](password) }),
    {} as Record<PasswordRuleKey, boolean>,
  );
}

/** Count of satisfied rules (0–5). */
export function passwordScore(password: string): number {
  return PASSWORD_RULE_KEYS.reduce(
    (score, key) => score + (RULE_TESTS[key](password) ? 1 : 0),
    0,
  );
}

/** Minimum satisfied rules required to submit — mirrors the legacy reset page. */
export const MIN_PASSWORD_SCORE = 3;

/**
 * Validation for the reset-password form. Enforces the legacy score>=3 policy
 * and password/confirm match. Output matches `confirmPasswordReset`.
 */
export function createResetPasswordSchema(t: Translate) {
  return z
    .object({
      password: z.string(),
      confirmPassword: z.string(),
    })
    // minLength is required, not merely scored: the backend binds this field to
    // Field(..., min_length=8) (api/auth/router_dynamic.py:78-79).
    .refine(
      (data) =>
        RULE_TESTS.minLength(data.password)
        && passwordScore(data.password) >= MIN_PASSWORD_SCORE,
      { message: t('auth.passwordRequirementsNotMet'), path: ['password'] },
    )
    .refine((data) => data.password === data.confirmPassword, {
      message: t('auth.passwordsDoNotMatch'),
      path: ['confirmPassword'],
    });
}

export type ResetPasswordFormValues = z.infer<
  ReturnType<typeof createResetPasswordSchema>
>;
